import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { Button, Divider, H, P, Wrapper } from '@farewill/ui'
import { BORDER, COLOR, GTR } from '@farewill/ui/tokens'

import ExternalLink from 'components/external-link'
import { getExizentCaseLink } from 'components/product-header/external-links'
import { formatDate } from 'lib/formatting/dates'
import { ProbateCase } from 'lib/models/probate-case'
import { ProbateEstate } from 'lib/models/probate-estate'
import { showModal } from 'state/actions'

import useEstateQuestionnaireStatus from '../hooks/use-estate-questionnaire-status'
import SendToExizentModal from './send-to-exizent-modal'

const DATE_FORMAT = 'D MMM YYYY, HH:mm'
const SEND_MODAL_MAX_WIDTH = 900

const StyledHeadingRow = styled(Wrapper)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${GTR.S};
`

const StyledReadOnlyCard = styled(Wrapper)`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${GTR.S};
  background-color: ${COLOR.STATE.ACTIVE_10};
  border-radius: ${BORDER.RADIUS.S};
  padding: ${GTR.S};
`

export type SendToExizentStatus = ReturnType<
  typeof useEstateQuestionnaireStatus
>

type Props = {
  probateCase: ProbateCase
  probateEstate: ProbateEstate
  sendToExizent: SendToExizentStatus
}

const EstateSummaryHeader = ({
  probateCase,
  probateEstate,
  sendToExizent,
}: Props) => {
  const dispatch = useDispatch()
  const { externalExizentCaseId } = probateCase.attributes
  const { isReadOnly, markSending, loading, failed } = sendToExizent

  const openSendModal = () =>
    dispatch(
      showModal({
        component: SendToExizentModal,
        headingText: 'Send to the Exizent case',
        maxWidth: SEND_MODAL_MAX_WIDTH,
        fitContentHeight: true,
        headingSize: 'M',
        config: {
          probateCaseId: probateCase.id,
          onSent: markSending,
        },
      })
    )

  return (
    <>
      <Wrapper>
        <StyledHeadingRow margin={[0, 0, 'S']}>
          <H size="M" margin={0}>
            Estate summary
          </H>
          {!isReadOnly && (
            <Button.Primary
              type="button"
              loading={loading}
              aria-busy={loading}
              disabled={loading}
              onClick={openSendModal}
            >
              Send to Exizent
            </Button.Primary>
          )}
        </StyledHeadingRow>
        {!isReadOnly && failed && (
          <P color={COLOR.STATE.ERROR} margin={[0, 0, GTR.XS]}>
            The last send to Exizent didn&apos;t work. Try sending again.
          </P>
        )}
        <P color={COLOR.GREY.DARK} margin={[0, 0, GTR.XS]}>
          Received from customer :{' '}
          {formatDate(probateCase.attributes.createdAt, false, DATE_FORMAT)}
        </P>
        <P color={COLOR.GREY.DARK} margin={0}>
          Last sync to Exizent :{' '}
          {formatDate(
            probateEstate.attributes.externalExizentLastSentAt,
            false,
            DATE_FORMAT
          ) ?? '-'}
        </P>
        {isReadOnly && (
          <StyledReadOnlyCard margin={['M', 0, 0]}>
            <div>
              <P size="S" strong color={COLOR.GREY.DARK} margin={[0, 0, 'XXS']}>
                This is a read-only view
              </P>
              <P size="S" color={COLOR.GREY.DARK} margin={0}>
                Refer to the Exizent case to make any changes or to see the most
                up-to-date case information.
              </P>
            </div>
            {externalExizentCaseId && (
              <Button.BorderedDark
                tag={ExternalLink}
                href={getExizentCaseLink(externalExizentCaseId)}
              >
                View Exizent case
              </Button.BorderedDark>
            )}
          </StyledReadOnlyCard>
        )}
      </Wrapper>
      <Divider margin={['L', 0]} />
    </>
  )
}

export default EstateSummaryHeader
